<template>
  <div
    v-if="isVisible"
    ref="filterModalRef"
    :style="{ position: 'absolute', top: top + 'px', right: right + 'px' }"
    class="filter-modal"
  >
    <span class="filter-modal-title modal-item">
      <p>{{ filterModalTitle }}</p>
    </span>
    <div class="filter-options">
      <label
        v-for="option in filterOptions"
        :key="option.key"
        class="modal-item"
      >
        <input
          type="checkbox"
          class="filter-checkbox-input"
          :value="option.key"
          v-model="selectedOptions"
          @change="emitSelectedOptions"
        />
        <span class="custom-checkbox"></span>
        <span class="filter-checkbox-label">
          {{ option.value }}
        </span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SearchbarModalOptions } from "@/shared/searchbarModalOptions";
import { onBeforeUnmount, onMounted, ref } from "vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    top: number;
    right: number;
    filterModalTitle: string;
    filterOptions: SearchbarModalOptions[];
  }>(),
  {
    filterModalTitle: "Filter By:",
  }
);

const emit = defineEmits(["close", "update:sort"]);
const filterModalRef = ref<HTMLElement | null>(null);
const selectedOptions = ref<string[]>([]);

const emitSelectedOptions = () => {
  // Check if all selected options are for the same field (e.g., "created_at")
  const getFieldName = (filter: string): string => {
    const firstColonIndex = filter.indexOf(":");
    return firstColonIndex !== -1 ? filter.substring(0, firstColonIndex) : filter;
  };

  // Group selections by field
  const fieldMap = new Map<string, string[]>();
  selectedOptions.value.forEach((option) => {
    const field = getFieldName(option);
    if (!fieldMap.has(field)) {
      fieldMap.set(field, []);
    }
    fieldMap.get(field)!.push(option);
  });

  // For fields with multiple selections, keep only the last selected one
  const filteredOptions: string[] = [];
  fieldMap.forEach((options, field) => {
    if (options.length > 1) {
      // Keep only the last selected option for this field
      filteredOptions.push(options[options.length - 1]);
      // Update selectedOptions to reflect this change
      selectedOptions.value = selectedOptions.value.filter(
        (opt) => getFieldName(opt) !== field || opt === options[options.length - 1]
      );
    } else {
      filteredOptions.push(...options);
    }
  });

  emit("update:sort", filteredOptions);
};

const handleClickOutside = (event: MouseEvent): void => {
  if (
    filterModalRef.value &&
    !filterModalRef.value.contains(event.target as Node) &&
    props.isVisible
  ) {
    emit("close");
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped src="./FilterModalMolecule.css"></style>
